import React, { useContext } from 'react';
import { LoginPopupExtended } from '@deporvillage/login-popup-component';
import { Config, getCookie } from '@deporvillage/utils-library-component';
import { track } from '@deporvillage/tracking-library-component';
import { SetLoader } from '../../../context/loader/actions';
import { LoaderContext } from '../../../context/loader/context';
import { CartContext } from '../../../context/cart/context';
import { SetTotalItems } from '../../../context/cart/actions';

let userData = null;

const getOrCreateUser = ({ store, country }) =>
  new Promise((res, rej) =>
    // eslint-disable-next-line no-undef
    fetch('/user/getorcreate', {
      method: 'POST',
      xhrFields: {
        withCredentials: true,
      },
      headers: {
        store,
        country,
      },
      credentials: 'include',
    })
      .then((ans) =>
        ans.json().then((json) => res({ status: ans.status, data: json })))
      .catch(/* istanbul ignore next */ (err) => rej(err)));

const startPurchase = ({ api, country }) =>
  new Promise((res, rej) =>
    // eslint-disable-next-line no-undef
    fetch(`${api}/api/purchase/start`, {
      method: 'POST',
      xhrFields: {
        withCredentials: true,
      },
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        country,
      }),
      credentials: 'include',
    })
      .then((ans) =>
        ans.json().then((json) => res({ status: ans.status, data: json })))
      .catch(/* istanbul ignore next */ (err) => rej(err)));

/* istanbul ignore next */
const getWishList = ({ api, store, session, id }) =>
    new Promise((res, rej) =>
        fetch(`${api}/wishlist/product/${id}?${Date.now()}`, {
            method: 'GET',
            xhrFields: {
                withCredentials: true,
            },
            headers: { store, sessionid: session },
        })
            .then((ans) => ans.json().then((json) => res({ status: ans.status, data: json })))
            .catch(/* istanbul ignore next */ (err) => rej(err)),
    );

/* istanbul ignore next */
const saveWishList = ({ api, store, session, id }) =>
    new Promise((res, rej) =>
        fetch(`${api}/wishlist`, {
            method: 'POST',
            xhrFields: {
                withCredentials: true,
            },
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                store,
                sessionid: session,
            },
            body: JSON.stringify({ product_id: id }),
        })
            .then((ans) => ans.text().then((text) => res({ status: ans.status, data: text })))
            .catch(/* istanbul ignore next */ (err) => rej(err)),
    );

/* istanbul ignore next */
const deleteWishList = ({ api, store, session, id }) =>
    new Promise((res, rej) =>
        fetch(`${api}/wishlist/product/${id}?${Date.now()}`, {
            method: 'DELETE',
            xhrFields: {
                withCredentials: true,
            },
            headers: { store, sessionid: session },
        })
            .then((ans) => ans.json().then((json) => res({ status: ans.status, data: json })))
            .catch(/* istanbul ignore next */ (err) => rej(err)),
    );

const postMessageToReskyt = (type, items) =>
    // eslint-disable-next-line no-undef
    window.parent.postMessage(`${type}-${items}`, /* eslint-disable no-trailing-spaces */ '*');

// istanbul ignore next
const postCustomerGoogleSignIn = async (api, country, token) => {
    const tokenMd5 = getCookie('token_md5');
    return fetch(`${api}/api/customer/social/google/sign-in`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            country: country.code,
            'dpv-session': getCookie('dpv-session'),
            ...(!tokenMd5 ? {} : { 'token-app': tokenMd5 }),
        },
        credentials: 'include',
        body: JSON.stringify({ accessToken: token }),
    })
        .then((ans) => ans)
        .catch((e) => console.log(e));
};

const UserTemplate = ({ children }) => {
  const {
 api, country, store, featureFlags, 
} = React.useContext(Config);
  const [processing, setProcessing] = React.useState(false);
  const [loginRedirectUrl, setLoginRedirectUrl] = React.useState(null);
  const [, dispatch] = React.useContext(LoaderContext);
  const [, dispatchCart] = useContext(CartContext);

    const getUserData = React.useCallback(() => {
        // istanbul ignore next
        if (!userData) return;
        // eslint-disable-next-line no-undef
        window.dispatchEvent(new CustomEvent('user:data', { detail: userData }));
        postMessageToReskyt('logged', !!userData?.logged_in);
    }, [userData]);

    const handleLoginPopupInit = (obj) => {
        // istanbul ignore next
        const redirectUrl = obj.detail && obj.detail.redirect ? obj.detail.redirect : '/';
        setLoginRedirectUrl(redirectUrl);
    };

    // istanbul ignore next
    const handleGoogleSignInSucess = async (e) => {
        if (e.data.type === 'app-google-sign-in:success') {
            showLoader(true);
            try {
                const res = await postCustomerGoogleSignIn(api, country, e?.data?.token);
                if (res.status === 200) {
                    window.location.reload();
                } else {
                    const error = await res.json();
                    showLoader(false);
                    console.log(error);
                }
            } catch (e) {
                return e;
            }
        }
    };

    // istanbul ignore next
    const handleGoogleSignInError = (e) => {
        if (e.data.type === 'app-google-sign-in:error') {
            console.log('error');
        }
    };

    const postWishList = (id, refresh) => {
        setProcessing(true);
        saveWishList({
            id,
            api,
            store: store.code,
            session: userData.session_id,
        })
            .then(() => {
                window.dispatchEvent(
                    new CustomEvent('wishlist:update', { detail: { isSaved: true } }),
                );
                postMessageToReskyt('offers', userData.favorites + 1);
                // istanbul ignore next
                if (refresh) window.location.reload();
            })
            .finally(() => setProcessing(false));
    };

    const handleAddToWishList = ({ detail: { id } }) => {
        if (processing || !id) return false;
        if (userData && userData.logged_in) {
            postWishList(id, false);
        } else {
            setLoginRedirectUrl(true);
        }
    };

    const handleDeleteWishList = ({ detail: { id } }) => {
        if (processing || !userData || !userData.logged_in || !id) return false;
        setProcessing(true);
        deleteWishList({
            id,
            api,
            store: store.code,
            session: userData.session_id,
        })
            .then((ans) => {
                window.dispatchEvent(
                    new CustomEvent('wishlist:update', {
                        detail: { isSaved: !ans },
                    }),
                );
                postMessageToReskyt('offers', userData.favorites);
            })
            .finally(() => setProcessing(false));
    };

    const handleGetWishList = ({ detail: { id } }) => {
        if (!id) return false;
        getWishList({
            id,
            api,
            store: store.code,
            session: userData.session_id,
        }).then((ans) => {
            window.dispatchEvent(
                new CustomEvent('wishlist:data', {
                    detail: { isSaved: ans.data && ans.data.length },
                }),
            );
        });
    };

  const handleWishListClick = ({ detail: { redirect } }) =>
    setLoginRedirectUrl(redirect);

    const handleSetUserData = (user) => {
        // istanbul ignore next
        if (!user) return;
        // TODO no se porque no podemos usar el state no hace el update correctamente
        userData = user;
        getUserData();
        // istanbul ignore next
        const cartItems = userData.cart
            ? userData.cart.items.reduce((p, { quantity }) => quantity + p, 0)
            : 0;
        postMessageToReskyt('cart', cartItems);
        postMessageToReskyt('offers', userData.favorites || 0);
        track({
            type: 'SET',
            name: 'setPage',
            data: {
                customer: {
                    id: userData.customer_id,
                    session_id: userData.session_id,
                },
            },
        });
        track({
            type: 'ACTION',
            name: 'CUSTOMER_LOGGED',
            data: {
                customer: {
                    id: userData.customer_id,
                    session_id: userData.session_id,
                },
            },
        });
    };

    // istanbul ignore next
    const showLoader = (show) => dispatch(SetLoader(show));

  React.useEffect(() => {
    getOrCreateUser({
      store: store.code,
      country: country.code,
    }).then((u) => {
        // @toDo FeatureFlag
        if (featureFlags?.enableStartPurchase) {
            startPurchase({
                api,
                country: country.code,
            }).then(({ data }) => {
                // @toDo FeatureFlag
                if (featureFlags?.enableNewAddToCart) {
                    if (data && Object.keys(data).length) {
                        dispatchCart(SetTotalItems(data.totalProductsQuantity));
                    }
                }
            });
        }

        handleSetUserData(u.data);
    });

        // eslint-disable no-undef
        window.addEventListener('user:getdata', getUserData);
        window.addEventListener('login-popup:init', handleLoginPopupInit);
        window.addEventListener('message', handleGoogleSignInSucess);
        window.addEventListener('message', handleGoogleSignInError);
        // TODO refactor this part here
        window.addEventListener('wishlist:add', handleAddToWishList);
        window.addEventListener('wishlist:del', handleDeleteWishList);
        window.addEventListener('wishlist:get', handleGetWishList);
        window.addEventListener('wishlist:click', handleWishListClick);

        return () => {
            window.removeEventListener('user:getdata', getUserData);
            window.removeEventListener('login-popup:init', handleLoginPopupInit);
            window.removeEventListener('message', handleGoogleSignInSucess);
            window.removeEventListener('message', handleGoogleSignInError);
            // TODO refactor this part here
            window.removeEventListener('wishlist:add', handleAddToWishList);
            window.removeEventListener('wishlist:del', handleDeleteWishList);
            window.removeEventListener('wishlist:get', handleGetWishList);
            window.removeEventListener('wishlist:click', handleWishListClick);
        };
        // eslint-enable no-undef
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    return (
        <React.Fragment>
            {loginRedirectUrl ? (
                <LoginPopupExtended
                    onClose={() => setLoginRedirectUrl(null)}
                    redirect={loginRedirectUrl}
                />
            ) : null}
            {children}
        </React.Fragment>
    );
};

export default UserTemplate;
